import React, { useCallback, useContext, useEffect, useState } from 'react';
import { EMPTY, forkJoin, Subscription } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';
import { useService } from '../../../services/services';
import { FormMode } from '../../../enums/form-mode';
import { EquipmentItem } from './interfaces/equipment-item';
import DashboardLoading from '../../dashboard/components/dashboard-loading';
import StaticTable, {
  StaticTableColumn,
} from '../../../components/common/table/static-table';
import { TextInputComponent } from '../../../components/form/elements/text';
import { DecimalInputComponent } from '../../../components/form/elements/decimal';
import {
  Option,
  SelectController,
} from '../../../components/form/elements/select';
import { DateInput } from '../../../components/form/elements/date-input';
import { useNotes } from './components/use-notes';
import { TableHeader } from './components/table-header';
import RemoveIcon from './components/remove-icon';
import Field from './components/field';
import { SchemaContext, StepProps } from './create';
import checkboxCss from '../../../components/form/elements/checkbox.module.css';
import css from './index.module.css';

export default function StepEquipment({ mode, setIsChildLoading }: StepProps) {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.vaad_liz_reports_v2.equipment.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId, id } = useParams<{ farmId: string; id: string }>();
  const { vaadLizReportV2Service: vaadLizReportService } = useService();
  const [tableData, setTableData] = useState<{ rows: EquipmentItem[] } | null>(
    null
  );
  const { getNotesElement, modal } = useNotes('equipment', mode);
  const { setSchema } = useContext(SchemaContext);
  const form = useFormContext();
  const { register, setValue } = form;

  const createSchema = useCallback(() => {
    return yup.object().shape({});
  }, []);

  const sortEquipmentItems = useCallback(
    (a: EquipmentItem, b: EquipmentItem): number => {
      const typeComparison = (a.equipment_type || '').localeCompare(
        b.equipment_type || '',
        'lv'
      );
      if (typeComparison !== 0) return typeComparison;

      const brandComparison = (a.brand || '').localeCompare(
        b.brand || '',
        'lv'
      );
      if (brandComparison !== 0) return brandComparison;

      const modelComparison = (a.model || '').localeCompare(
        b.model || '',
        'lv'
      );
      if (modelComparison !== 0) return modelComparison;

      return a.id.localeCompare(b.id);
    },
    []
  );

  const onDataError = useCallback(
    (error) => {
      console.error('Error fetching data', error);
      setIsChildLoading(false);
    },
    [setIsChildLoading]
  );

  const onDataLoad = useCallback(
    (data: EquipmentItem[], subtypes: Option[]) => {
      const remove = (equipmentId: string) => {
        setIsChildLoading(true);
        return vaadLizReportService
          .removeEquipment(farmId, id, equipmentId)
          .pipe(
            tap((data) => onDataLoad(data, subtypes)),
            catchError((error) => {
              onDataError(error);
              return EMPTY;
            }),
            finalize(() => setIsChildLoading(false))
          );
      };

      data.sort(sortEquipmentItems);

      const formValues = { equipment: {} };
      const tableData: EquipmentItem[] = data.map((row: EquipmentItem, i) => {
        const name = `equipment.${row.id}`;
        formValues.equipment[row.id] = { ...row };

        const tableRow = { ...row };
        tableRow.equipment_subtype = (
          <SelectController
            name="equipment_subtype"
            multi={false}
            options={subtypes}
            cacheOn={[]}
            menuPlacement={i + 1 === data.length ? 'top' : 'bottom'}
            grouped={false}
            disabled={mode === FormMode.view}
            clearable
            asyncOptions={null}
            parentName={`equipment.${row.id}`}
          />
        );
        tableRow.exact_equipment = (
          <div>
            <input
              type="checkbox"
              name={name + '.exact_equipment'}
              className={checkboxCss['checkbox']}
              {...register(name + '.exact_equipment')}
              disabled={mode === FormMode.view}
            />
          </div>
        );
        tableRow.registration_number = (
          <Field name={name + '.registration_number'}>
            <TextInputComponent
              name={name + '.registration_number'}
              disabled={mode === FormMode.view}
            />
          </Field>
        );
        tableRow.date_of_purchase = (
          <DateInput
            name={name + '.date_of_purchase'}
            disabled={mode === FormMode.view}
          />
        );
        tableRow.capacity_t = (
          <DecimalInputComponent
            name={name + '.capacity_t'}
            disabled={mode === FormMode.view}
            negative={false}
          />
        );
        tableRow.width_m = (
          <DecimalInputComponent
            name={name + '.width_m'}
            disabled={mode === FormMode.view}
            negative={false}
          />
        );
        tableRow.spreader_company_name = (
          <TextInputComponent
            name={name + '.spreader_company_name'}
            disabled={mode === FormMode.view}
          />
        );
        tableRow.notes_edit = getNotesElement(name, tableRow);

        if (mode !== FormMode.view) {
          tableRow.remove = <RemoveIcon id={tableRow.id} onRemove={remove} />;
        }

        return tableRow;
      });

      setTableData({ rows: tableData });
      setValue('equipment', formValues['equipment']);
      setSchema(createSchema());
      setIsChildLoading(false);
    },
    [
      sortEquipmentItems,
      setIsChildLoading,
      createSchema,
      setSchema,
      setValue,
      vaadLizReportService,
      farmId,
      id,
      onDataError,
      mode,
      register,
      getNotesElement,
    ]
  );

  useEffect(() => {
    setIsChildLoading(true);

    const subscription: Subscription = forkJoin({
      equipmentData: vaadLizReportService.getEquipment(farmId, id),
      equipmentSubtypes: vaadLizReportService.getEquipmentSubtypes(farmId),
    }).subscribe(({ equipmentData, equipmentSubtypes }) => {
      const subtypes: Option[] = Object.keys(equipmentSubtypes).map(
        (key): Option => ({
          label: equipmentSubtypes[key],
          value: key,
        })
      );
      // subtypes.unshift({label: ' ', value: ''});

      onDataLoad(equipmentData, subtypes);
    }, onDataError);

    return () => subscription.unsubscribe();
  }, [
    farmId,
    id,
    onDataError,
    onDataLoad,
    setIsChildLoading,
    vaadLizReportService,
  ]);

  const columns = useCallback(() => {
    const baseColumns: StaticTableColumn<EquipmentItem>[] = [
      {
        accessor: 'equipment_type',
        header: tp('equipment_type'),
        className: css['strong'],
      },
      {
        accessor: 'exact_equipment',
        header: (
          <TableHeader
            headerText={tp('exact_equipment')}
            tooltipId="exact_equipment-tooltip"
            tooltipText="pages.crop.vaad_liz_reports_v2.equipment.tooltip.exact_equipment"
          />
        ),
        className: 'text-center',
      },
      { accessor: 'equipment_subtype', header: tp('equipment_subtype') },
      { accessor: 'brand', header: tp('brand') },
      { accessor: 'model', header: tp('model') },
      {
        accessor: 'registration_number',
        header: (
          <TableHeader
            headerText={tp('registration_number')}
            tooltipId="registration_number-tooltip"
            tooltipText="pages.crop.vaad_liz_reports_v2.equipment.tooltip.registration_number"
          />
        ),
      },
      {
        accessor: 'date_of_purchase',
        header: (
          <TableHeader
            headerText={tp('date_of_purchase')}
            tooltipId="date_of_purchase-tooltip"
            tooltipText="pages.crop.vaad_liz_reports_v2.equipment.tooltip.date_of_purchase"
          />
        ),
      },
      {
        accessor: 'capacity_t',
        header: (
          <TableHeader
            headerText={tp('capacity_t')}
            tooltipId="capacity_t-tooltip"
            tooltipText="pages.crop.vaad_liz_reports_v2.equipment.tooltip.capacity_t"
          />
        ),
      },
      {
        accessor: 'width_m',
        header: (
          <TableHeader
            headerText={tp('width_m')}
            tooltipId="width_m-tooltip"
            tooltipText="pages.crop.vaad_liz_reports_v2.equipment.tooltip.capacity_t"
          />
        ),
      },
      {
        accessor: 'spreader_company_name',
        header: tp('spreader_company_name'),
      },
      { accessor: 'notes_edit', header: tp('notes') },
    ];

    if (mode !== FormMode.view) {
      baseColumns.push({ accessor: 'remove', header: tp('remove') });
    }

    return baseColumns;
  }, [tp, mode]);

  return !tableData ? (
    <DashboardLoading />
  ) : (
    <div className={`${css['table']} ${css['equipment-table']}`}>
      <StaticTable<EquipmentItem>
        data={tableData}
        keyAccessor="id"
        columns={columns()}
        fixedHeight
      />

      {modal}
    </div>
  );
}
